import React from "react";
import {
  IconCube,
  IconDatabaseOff,
  IconSearchOff,
  IconStack2,
  IconWorldOff,
} from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react-lite";
import { productSearchStore as store } from "../ProductSearchStore";
import { ProductSourceEnum, ProductType } from "api-client";
import Button from "components/Button";
import { useNavigate } from "react-router";
import useProductActions from "../useProductActions";
import { ROUTES_ENUM } from "routes/consts";
import ComponentsTreeIcon from "assets/images/components-tree.svg";
import clsx from "clsx";
import { COEStore } from "features/ComponentEditor/ComponentEditorStore";
import Alert from "components/Alert";

const EmptyState = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { saveStateAndGetPath } = useProductActions();

  const redirectToCreateProduct = () => {
    const pathname = saveStateAndGetPath();
    navigate(`${pathname}/${ROUTES_ENUM.PRODUCT_EDITOR}`);
  };

  const openComponentEditor = () => navigate(`${ROUTES_ENUM.COMPONENT_EDITOR}`);

  function showError() {
    // Server Error
    if (store.hasServerError) {
      return (
        <>
          <IconDatabaseOff className="w-[76px] h-[76px] text-gray-200 mb-4" />
          <div className="text-xs font-medium text-gray-500 max-w-[650px] text-center">
            {t("productSearch.serverError")}
          </div>
        </>
      );
    }

    // No Matching products
    if (store.hasAnyFilterSet()) {
      return (
        <>
          <IconSearchOff className="w-[76px] h-[76px] text-gray-200 mb-4" />
          <div className="text-xs font-medium text-gray-500 max-w-[650px] text-center">
            {t("productSearch.noProductFound")}
          </div>
        </>
      );
    }

    // No Custom products & Components
    if (
      store.searchInput.product_source === ProductSourceEnum.Custom &&
      !store.searchInput.type
    ) {
      return (
        <>
          <div className="flex items-center mb-4">
            <IconCube
              stroke={2}
              size={64}
              className="bg-teal-300 rounded-full p-2 mr-4 text-teal-900"
            />
            <IconStack2
              stroke={2}
              size={64}
              className="bg-cyan-300 rounded-full p-2 text-cyan-900"
            />
          </div>
          <div className="text-xs font-medium text-gray-500 max-w-[650px] text-center">
            {t("productSearch.noBuildingProductFound")}
          </div>
          <Button
            width="fit-content"
            type="primary"
            className="mt-8"
            onClick={redirectToCreateProduct}
          >
            {t("mapping.createBuildingProduct")}
          </Button>
        </>
      );
    }

    // No Custom building products
    if (
      store.searchInput.product_source === ProductSourceEnum.Custom &&
      store.searchInput.type === ProductType.Product
    ) {
      return (
        <>
          <div className="flex items-center mb-4">
            <IconCube
              stroke={2}
              size={64}
              className="bg-teal-300 rounded-full p-2 text-teal-900"
            />
          </div>
          <div className="text-xs font-medium text-gray-500 max-w-[650px] text-center">
            {t("productSearch.noBuildingProductFound")}
          </div>
          <Button
            width="fit-content"
            type="primary"
            className="mt-8"
            onClick={redirectToCreateProduct}
          >
            {t("mapping.createBuildingProduct")}
          </Button>
        </>
      );
    }

    // No Custom components opened search in COE
    if (
      store.searchInput.product_source === ProductSourceEnum.Custom &&
      store.searchInput.type === ProductType.Component &&
      COEStore.openedSearchInCE
    ) {
      return (
        <div className="flex max-w-[600px] items-center mb-3">
          <Alert
            type="info"
            className="bg-white p-0 border-none items-center"
            description={t("productSearch.emptyStateComponentsInCE")}
          />
        </div>
      );
    }

    // No Custom components
    if (
      store.searchInput.product_source === ProductSourceEnum.Custom &&
      store.searchInput.type === ProductType.Component
    ) {
      return (
        <>
          <div className="flex items-center">
            <div className="flex items-center mr-8">
              <img src={ComponentsTreeIcon} alt="components-tree" />
            </div>
            <div>
              <div className="text-xs font-medium text-gray-700 max-w-[460px] mb-2">
                {t("productSearch.noComponentFound")}
              </div>
              <div className="text-xs font-medium text-gray-500 max-w-[460px]">
                {t("productSearch.noComponentFound2")}
              </div>
            </div>
          </div>
          <div className="flex mt-8 gap-4">
            <Button
              width="fit-content"
              type="secondary"
              onClick={redirectToCreateProduct}
            >
              {t("mapping.createBuildingProduct")}
            </Button>
            <Button
              width="fit-content"
              type="primary"
              onClick={openComponentEditor}
            >
              {t("mapping.createComponent")}
            </Button>
          </div>
        </>
      );
    }

    // No Manufacturers products
    if (
      store.searchInput.product_source?.toLowerCase() ===
      ProductSourceEnum.Manufacturer.toLowerCase()
    ) {
      return (
        <>
          <IconWorldOff className="w-[76px] h-[76px] text-gray-200 mb-4" />
          <div className="text-xs font-medium text-gray-500 max-w-[650px] text-center">
            {!store.searchInput.type &&
              t("productSearch.noProductManufacturer")}
            {store.searchInput.type === ProductType.Product &&
              t("productSearch.noBuildingProductManufacturer")}
            {store.searchInput.type === ProductType.Component &&
              t("productSearch.noComponentManufacturer")}
          </div>
        </>
      );
    }
  }

  return (
    <div
      className={clsx(
        "relative border border-gray-300 rounded-md overflow-hidden flex-grow flex items-center justify-center",
        "min-h-[calc(100vh-256px)] max-h-[calc(100vh-256px)]"
      )}
    >
      <div className="flex flex-col items-center justify-center">
        {showError()}
      </div>
    </div>
  );
};

export default observer(EmptyState);
